// ── useRingFeed — the data of one ring, for the ring display and the overlay ──
// #604: the ring display (a TV at the ring) and the broadcast overlay (a source
// of a live stream) render from this one hook. So they always show the same
// data. The hook is silent: a failed read goes to telemetry, never to a toast.
// The pages mount OUTSIDE the app's `<ToastProvider>` on purpose.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, useCallback } from 'react';
import { errorCode, errorStatus } from '../lib/httpError';
import { getBouts, getBout, getBoutTimer, getBoutScores, getRingNextBout, publicApi } from '../api/client';
import { useSocket, useRooms, bindSocket, joinBoutRoom, leaveBoutRoom } from './useSocket';
import { logBackgroundError } from '../observability';
import { useOnResume } from '../lib/resume';
import type {
  Bout, BoutVerdict, BoutTimerPhase, BoutTimerState, PublicRoundScore,
  SocketBoutScored, SocketBoutAdvanced, SocketBoutDisplayRoundsUpdated, SocketBoutPublicScoresUpdated, SocketBoutTimer,
} from '../types';

// The reads of the scores, the timer, the next bout and the verdict go through
// `publicApi`. It has no session cookie and no interceptor, so an admin on the
// TV gets the public view of the scores. The bout list is the one read on the
// signed-in client, because the route adds the level fields for an official.

// The verdict of one bout, or null (#454). `GET /api/bouts/:id` carries it
// for a guest, as the result of a scored bout is public. `undefined` = the
// read FAILED, so the answer is unknown (#457): the caller keeps the screen,
// and the next poll reads again. Only a 404 (the bout is gone) means "no
// result".
async function getBoutVerdict(boutId: string, context: string): Promise<BoutVerdict | null | undefined> {
  try {
    const { data } = await getBout(boutId, publicApi);
    return data?.verdict ?? null;
  } catch (err) {
    if (errorStatus(err) === 404) return null;
    logBackgroundError(context)(err);
    return undefined;
  }
}

/** The number of failed reads of a verdict. After this number, the screen shows the next bout with no result. */
const VERDICT_TRIES = 3;

/** The time of a result on screen. Then the screen shows the next bout: a
 *  short splash, as the countdown of the bout page (operator, 2026-10-15). */
const RESULT_MS = 8_000;

/** #627 — the end of a result reads the live bout. A failed read keeps the
 *  result and its NEXT on screen, and the hook reads again after
 *  `RESULT_RETRY_MS`. After `RESULT_END_TRIES` failed reads, the screen shows
 *  no live bout, and the 10 s poll reads again. */
const RESULT_RETRY_MS = 3_000;
const RESULT_END_TRIES = 3;

/** The timer on screen: the state of the server, and the time of this screen when it came. */
interface TimerOnScreen extends BoutTimerState { boutId: string; at: number }

const leftOf = (s: TimerOnScreen, now: number) =>
  s.phase === 'running' ? Math.max(0, s.left_ms - Math.max(0, now - s.at)) : s.left_ms;

/** A read keeps the timer on screen when it gives the same state within 1 s.
 *  The network delay of the poll must not make the countdown jump. */
function takeTimer(prev: TimerOnScreen | null, boutId: string, next: BoutTimerState, at: number): TimerOnScreen {
  const fresh = { boutId, shown: next.shown, phase: next.phase, left_ms: next.left_ms, started: next.started, at };
  if (!prev || prev.boutId !== boutId || prev.shown !== next.shown || prev.phase !== next.phase || prev.started !== next.started) return fresh;
  return Math.abs(leftOf(prev, at) - next.left_ms) < 1000 ? prev : fresh;
}

export interface RingFeed {
  ringName: string;
  /** The live bout of the ring; during a result, the bout of the result. */
  currentBout: Bout | null;
  /** The approved rounds of the live bout (the public view). */
  publicScores: PublicRoundScore[];
  /** The result of the bout on screen, for `RESULT_MS`. */
  flashResult: BoutVerdict | null;
  /** The time left of the timer of the jury while the jury shows it, else null. */
  timerLeft: number | null;
  timerPhase: BoutTimerPhase;
  /** The next bout of the ring (#604, #608). It waits (pending). During a
   *  result, it can be the bout that the auto-advance already made live. It
   *  is never `currentBout`. Both ring screens show it while no bout is live
   *  (the NEXT card, "Next"). The ring display also shows it in its NEXT strip. */
  nextBout: Bout | null;
}

/** The data of one ring. `name` names the page in the telemetry. With
 *  `next`, the hook also reads the next bout of the ring and the name of the
 *  ring (#608: both ring screens pass it). */
export function useRingFeed(
  tournamentId: string | undefined, ringId: string | undefined, name: 'RingDisplay' | 'RingOverlay',
  { next: withNext = false }: { next?: boolean } = {},
): RingFeed {
  const socket = useSocket();

  const [ringName, setRingName]       = useState('');
  const [currentBout, setCurrentBout] = useState<Bout | null>(null);
  const [publicScores, setPublicScores] = useState<PublicRoundScore[]>([]);
  const [flashResult, setFlashResult]  = useState<BoutVerdict | null>(null);
  const [bouts, setBouts] = useState<Bout[]>([]);
  const [nextBoutId, setNextBoutId] = useState<string | null>(null);

  // #601 — the timer of the jury, while the jury shows it.
  const [timer, setTimer] = useState<TimerOnScreen | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /** The time of the newest `bout:timer` event: a read that started before it is old. */
  const timerEventAt = useRef(0);

  const currentBoutIdRef = useRef<string | null>(null);
  const flashingRef      = useRef(false);
  const flashTimer       = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The count of failed reads of the verdict of the bout on screen (logic
   *  review of #604). After VERDICT_TRIES failed reads, the screen shows the
   *  live bout of the ring, and the old bout gets no result. A read that fails
   *  each time must not keep an old bout on screen all day. */
  const verdictFails     = useRef<{ boutId: string; count: number }>({ boutId: '', count: 0 });
  /** The count of the results that started. A read that started before a
   *  result is old, also when it answers after the result: a list from before
   *  a verdict brought the finished bout back as live (logic review of #608). */
  const flashGen         = useRef(0);

  // ── Load current bout for this ring ───────────────────────────────────────
  // `null` = the ring has no live bout. `undefined` = the load FAILED, so the
  // answer is unknown (#457). One failed poll on venue Wi-Fi turned into
  // `null`, and the TV showed "Waiting for next bout…" in the middle of a bout.
  /** The tournament id that the server refused as invalid (400 INVALID_UUID).
   *  The answer does not change, so the hook does not read the list again. */
  const listRefused = useRef('');
  const loadCurrentBout = useCallback(async (): Promise<Bout | null | undefined> => {
    if (!tournamentId || !ringId || listRefused.current === tournamentId) return null;
    try {
      const { data: list } = await getBouts(tournamentId);
      setBouts(list);
      const ringBout = list.find(b => b.ring_id === ringId);
      if (ringBout?.ring_name) setRingName(ringBout.ring_name);
      return list.find(b => b.ring_id === ringId && b.status === 'current') ?? null;
    } catch (err) {
      // The backend records the refusal with its code, so the hook does not
      // log it at each poll (observability review of #608).
      if (errorStatus(err) === 400 && errorCode(err) === 'INVALID_UUID') {
        listRefused.current = tournamentId;
        return null;
      }
      logBackgroundError(`${name}:bouts`)(err);
      return undefined;
    }
  }, [tournamentId, ringId, name]);

  // A failed load keeps the scores on screen; the next poll loads again.
  // #627 — each read has a number. An answer of a read that started before
  // the read on screen is old: a socket event and the poll can read at the
  // same time, and the older answer can come last.
  const scoresSeq = useRef(0);
  const scoresShown = useRef(0);
  const loadScores = useCallback(async (boutId: string) => {
    const seq = ++scoresSeq.current;
    try {
      const { data } = await getBoutScores(boutId, publicApi);
      // The scores of a bout that left the screen do not apply.
      if (currentBoutIdRef.current !== boutId || seq < scoresShown.current) return;
      scoresShown.current = seq;
      // No cookie goes with the read, so the view is 'public'.
      setPublicScores(data.view === 'public' ? data.scores : []);
    } catch (err) { logBackgroundError(`${name}:scores`)(err); }
  }, [name]);

  // A failed read keeps the timer on screen; the next poll reads again. A
  // read applies only when no event came after it started, and only while
  // its bout is on screen (logic-reviewer §15, a late answer).
  const loadTimer = useCallback(async (boutId: string) => {
    const started = Date.now();
    try {
      const { data } = await getBoutTimer(boutId, publicApi);
      if (timerEventAt.current >= started || currentBoutIdRef.current !== boutId) return;
      setTimer(prev => takeTimer(prev, boutId, data, Date.now()));
    } catch (err) { logBackgroundError(`${name}:timer`)(err); }
  }, [name]);

  // #604, #608 — the next bout of the ring, also during a live bout (the NEXT
  // strip of the ring display). A failed read keeps the bout on screen. The
  // hook ignores an older read that answers after a newer one. The answer
  // also names the ring, so a ring with no bout shows its name.
  const nextSeq = useRef(0);
  /** The address whose ring the route refused (404 or 400). The answer does
   *  not change, so the hook stops this read for that address. */
  const nextRefused = useRef('');
  const loadNext = useCallback(async () => {
    if (!withNext || !tournamentId || !ringId || nextRefused.current === `${tournamentId}/${ringId}`) return;
    const seq = ++nextSeq.current;
    try {
      const { data } = await getRingNextBout(tournamentId, ringId);
      if (seq !== nextSeq.current) return;
      setNextBoutId(data.id);
      if (data.ring_name) setRingName(data.ring_name);
    } catch (err) {
      if (seq !== nextSeq.current) return;
      // A 404 or a 400 is a refusal that the backend records with its code,
      // so the hook does not log it. The refusal of the route itself does not
      // change: the ring is not in the tournament, or the address holds no
      // id. So the hook does not read again. Another 404 (a backend without
      // the route, during a release) gets a read at the next poll.
      const status = errorStatus(err);
      if (status === 404 || status === 400) {
        const code = errorCode(err);
        if (code === 'RING_NOT_IN_TOURNAMENT' || code === 'INVALID_UUID') nextRefused.current = `${tournamentId}/${ringId}`;
        setNextBoutId(null);
        return;
      }
      logBackgroundError(`${name}:next`)(err);
    }
  }, [withNext, tournamentId, ringId, name]);

  // #454 — ONE way to show a result. It came from `bout:scored` only, and
  // only while that bout was on screen: a `bout:advanced` or a poll that
  // arrived first moved the TV to the next bout, and a dropped socket missed
  // the event, so the winner never showed.
  const flash = useCallback((verdict: BoutVerdict) => {
    flashingRef.current = true;
    // A read of the next bout that started before the result is old. The
    // auto-advance can start that bout, and then the read names the bout
    // after it (logic review of #608).
    nextSeq.current++;
    flashGen.current++;
    setFlashResult(verdict);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    const end = (delay: number, failed: number) => {
      const ending: ReturnType<typeof setTimeout> = setTimeout(async () => {
        // Read first. Then change the screen in one step. The finished bout
        // must not show as live while the read runs (logic review of #608).
        const read = await loadCurrentBout();
        // A newer result started its own timer during the read: it wins.
        if (flashTimer.current !== ending) return;
        // #627 — a failed read: the live bout is unknown. The screen keeps the
        // result and its NEXT. Before, it showed no live bout, and the NEXT
        // card named the bout that the auto-advance had made live.
        if (read === undefined && failed + 1 < RESULT_END_TRIES) { end(RESULT_RETRY_MS, failed + 1); return; }
        const next = read ?? null;
        setFlashResult(null);
        flashingRef.current = false;
        if (currentBoutIdRef.current) leaveBoutRoom(currentBoutIdRef.current);
        currentBoutIdRef.current = null;
        setPublicScores([]);
        setTimer(null);
        setCurrentBout(next);
        if (next) {
          currentBoutIdRef.current = next.id;
          joinBoutRoom(next.id);
          loadScores(next.id);
          loadTimer(next.id);
          loadNext();
        } else {
          setPublicScores([]);
          loadNext();
        }
      }, delay);
      flashTimer.current = ending;
    };
    end(RESULT_MS, 0);
  }, [loadCurrentBout, loadScores, loadTimer, loadNext]);

  const refresh = useCallback(async () => {
    if (flashingRef.current) return;
    const gen = flashGen.current;
    const bout = await loadCurrentBout();
    // A result can start during the read. Then the read is old, and the
    // result wins (security review of #608). It stays old after the result.
    if (flashingRef.current || gen !== flashGen.current) return;
    // Unknown: keep what is on screen. The next poll (10 s) or event retries.
    if (bout === undefined) return;
    // The live bout of the ring changed while no result was on screen: the
    // result event was missed. Show the result of the bout on screen first.
    const shown = currentBoutIdRef.current;
    if (shown && bout?.id !== shown) {
      const verdict = await getBoutVerdict(shown, `${name}:verdict`);
      // A socket event can start a flash during the await: it wins.
      if (flashingRef.current || gen !== flashGen.current) return;
      // Unknown: keep the bout on screen for the next poll. Before the fix, the
      // move to the next bout showed no result at all (#454). After
      // VERDICT_TRIES failed reads (about 30 s), the screen shows the next bout.
      if (verdict === undefined) {
        const f = verdictFails.current;
        verdictFails.current = { boutId: shown, count: f.boutId === shown ? f.count + 1 : 1 };
        if (verdictFails.current.count < VERDICT_TRIES) return;
      }
      // An answer, or the move to the next bout: the count starts again.
      verdictFails.current = { boutId: '', count: 0 };
      if (verdict) { flash(verdict); return; }
    }
    setCurrentBout(bout);
    if (bout) {
      if (bout.id !== currentBoutIdRef.current) {
        if (currentBoutIdRef.current) leaveBoutRoom(currentBoutIdRef.current);
        currentBoutIdRef.current = bout.id;
        joinBoutRoom(bout.id);
        setPublicScores([]);
        setTimer(null);
      }
      loadScores(bout.id);
      loadTimer(bout.id);
      loadNext();
    } else {
      currentBoutIdRef.current = null;
      setPublicScores([]);
      setTimer(null);
      loadNext();
    }
  }, [loadCurrentBout, loadScores, loadTimer, loadNext, flash, name]);

  // Initial load
  useEffect(() => { refresh(); }, [refresh]);

  // #499 — after a gap in the socket, at once instead of at the next poll.
  useOnResume(refresh, name === 'RingDisplay' ? 'ring-display' : 'ring-overlay');

  // Poll every 10 s as socket fallback
  useEffect(() => {
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [refresh]);

  // ── Socket events ──────────────────────────────────────────────────────────
  // #664 (F49) — the tournament room is a key. The bout room of the live bout
  // changes with the bout on screen and its id is stored in a ref, so
  // `refresh`, `flash` and the cleanup below join and leave it with direct
  // calls.
  useRooms([tournamentId ? `tournament:${tournamentId}` : null]);
  useEffect(() => {
    if (!socket || !tournamentId) return;

    function onStatusChanged() { refresh(); }

    // The server sends `bout:scored` first since #454, and `bout:advanced`
    // carries the verdict too, for a TV that missed the first event.
    function onAdvanced({ completedBoutId, verdict }: SocketBoutAdvanced) {
      if (completedBoutId && completedBoutId === currentBoutIdRef.current && verdict && !flashingRef.current) {
        flash(verdict);
        return;
      }
      refresh();
    }

    function onScored({ boutId, verdict }: SocketBoutScored) {
      if (boutId !== currentBoutIdRef.current) return;
      flash(verdict);
    }

    function onDisplayUpdated({ boutId }: SocketBoutDisplayRoundsUpdated) {
      if (boutId === currentBoutIdRef.current) loadScores(boutId);
    }

    // #604 — a changed total of an approved round shows at once, not at the next poll.
    function onPublicScores({ boutId }: SocketBoutPublicScoresUpdated) {
      if (boutId === currentBoutIdRef.current) loadScores(boutId);
    }

    // #601 — each change of the timer of the jury. The screen counts down from it.
    function onTimer({ boutId, shown, phase, left_ms, started }: SocketBoutTimer) {
      if (boutId !== currentBoutIdRef.current) return;
      const at = Date.now();
      timerEventAt.current = at;
      setTimer({ boutId, shown, phase, left_ms, started, at });
    }

    const unbind = bindSocket(socket, {
      'bout:status_changed': onStatusChanged,
      'bout:advanced': onAdvanced,
      'bout:scored': onScored,
      'bout:display_rounds_updated': onDisplayUpdated,
      'bout:public_scores_updated': onPublicScores,
      'bout:timer': onTimer,
    });

    return () => {
      if (currentBoutIdRef.current) leaveBoutRoom(currentBoutIdRef.current);
      unbind();
    };
  }, [socket, tournamentId, refresh, flash, loadCurrentBout, loadScores]);

  // #601 — the tick of the countdown, only while the time runs.
  const ticking = !!timer?.shown && timer.phase === 'running';
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [ticking]);

  // Cleanup flash timer on unmount
  // If the page unmounts during the read at the end of a result, the callback stops at its identity check.
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); flashTimer.current = null; }, []);

  // #601 — the timer of the jury, for the bout on screen only.
  const timerLeft = timer && timer.shown && timer.boutId === currentBout?.id ? leftOf(timer, now) : null;
  const timerPhase: BoutTimerPhase = timer && timerLeft === 0 && timer.phase === 'running' ? 'done' : timer?.phase ?? 'ready';

  return {
    ringName, currentBout, publicScores, flashResult, timerLeft, timerPhase,
    // A read from before a start can still name the bout that is live now.
    // The next bout waits (pending), and it is not the bout on screen.
    // During a result, the next bout can already be live: the auto-advance
    // starts it with the verdict, and a poll can read the list first.
    nextBout: nextBoutId && nextBoutId !== currentBout?.id
      ? bouts.find(b => b.id === nextBoutId && (b.status === 'pending' || (!!flashResult && b.status === 'current'))) ?? null
      : null,
  };
}
